/** @file dock-manager/BookingCard.jsx — a single booking pill in a bay lane. */
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { BOOKING_STATUSES, STATUS_BADGE_CLASS } from "@/lib/constants";
import BookingPeek from "./BookingPeek";
import { fmtClock } from "./formatters";

export default function BookingCard({ booking, bay, windowStart, windowEnd, pxPerHour, onClick }) {
  const start = new Date(booking.scheduled_start);
  const end = new Date(booking.scheduled_end);
  // Clip to window
  const visStart = start < windowStart ? windowStart : start;
  const visEnd = end > windowEnd ? windowEnd : end;
  const leftHours = (visStart - windowStart) / 3600 / 1000;
  const widthHours = Math.max(0.5, (visEnd - visStart) / 3600 / 1000); // min 30-min visual

  const status = BOOKING_STATUSES.find((s) => s.value === booking.status);
  const baseColor = bay?.color || "#3b82f6";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          data-testid={`booking-card-${booking.id}`}
          className="absolute top-1.5 bottom-1.5 rounded-md text-left text-white shadow-sm overflow-hidden ring-1 ring-black/5 hover:ring-2 hover:ring-slate-900 transition"
          style={{
            left: leftHours * pxPerHour,
            width: widthHours * pxPerHour - 2,
            backgroundColor: baseColor,
          }}
        >
          <div className="px-1.5 py-1 h-full flex flex-col justify-between">
            <div className="flex items-center gap-1 text-[10px] leading-tight">
              <span className="uppercase font-semibold">{booking.direction === "inbound" ? "IN" : "OUT"}</span>
              <span className="opacity-70">·</span>
              <span className="truncate font-medium">
                {(booking.direction === "outbound"
                  ? (booking.receiver_company || booking.receiver_name)
                  : (booking.sender_company || booking.sender_name)) || "—"}
              </span>
            </div>
            <div className="text-[10px] truncate leading-tight opacity-90">
              {driverShort(booking)}{booking.vehicle_regos?.[0] ? ` · ${booking.vehicle_regos[0]}` : ""}
            </div>
            <div className="flex justify-between items-center gap-1">
              <span className="text-[10px] opacity-80 tabular-nums">
                {fmtClock(start)} – {fmtClock(end)}
              </span>
              {status && (
                <span
                  className={`text-[9px] px-1 rounded ${STATUS_BADGE_CLASS[status.tone]}`}
                  style={{ color: undefined }}
                >
                  {status.label}
                </span>
              )}
            </div>
          </div>
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        <BookingPeek booking={booking} />
      </TooltipContent>
    </Tooltip>
  );
}

function driverShort(b) {
  // Pulled via Supabase relation: booking.driver = { first_name, last_name }
  const first = (b.driver?.first_name || b.driver_first_name || "").trim();
  const last  = (b.driver?.last_name  || b.driver_last_name  || "").trim();
  if (first || last) return `${first} ${last ? last[0] + "." : ""}`.trim();
  return "";
}

